import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingBag, User, LogOut, MapPin, ChevronDown, Search, Heart, Sun, Moon, Package, Mail, UserPlus } from 'lucide-react';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useCart } from '../../context/CartContext';
import { categoryAPI } from '../../services/api';

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [megaOpen, setMegaOpen] = useState(null);
    const [categories, setCategories] = useState([]);
    const [mobileExpanded, setMobileExpanded] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { settings } = useSiteSettings();
    const { customer, logout, profileIncomplete } = useCustomerAuth();
    const { cart, setSidebarOpen } = useCart();
    const profileRef = useRef(null);
    const megaTimerRef = useRef(null);

    // Theme
    const [theme, setTheme] = useState(() => {
        if (typeof window !== 'undefined') return localStorage.getItem('theme') || 'light';
        return 'light';
    });
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);
    const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

    // Fetch parent categories with subcategories
    useEffect(() => {
        categoryAPI.getAll({ active: 'true' })
            .then(res => setCategories(res.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
        setProfileOpen(false);
        setMegaOpen(null);
    }, [location]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    useEffect(() => {
        const handleClick = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const handleLogout = () => {
        logout();
        setProfileOpen(false);
        navigate('/');
    };

    const [searchQuery, setSearchQuery] = useState('');

    const handleSearch = useCallback((e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/urunler?search=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
        }
    }, [searchQuery, navigate]);

    const parentCats = categories.filter(c => !c.parentId);

    const openMega = (id) => {
        clearTimeout(megaTimerRef.current);
        setMegaOpen(id);
    };
    const closeMega = () => {
        megaTimerRef.current = setTimeout(() => setMegaOpen(null), 120);
    };

    const staticLinks = [
        { to: '/urunler', label: 'Koleksiyonlar' },
        { to: '/hakkimizda', label: 'Hakkımızda' },
        { to: '/iletisim', label: 'İletişim' },
    ];

    return (
        <>
            <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
                <div className="navbar-row">
                    {/* Mobile hamburger - left */}
                    <button
                        className={`mobile-toggle ${menuOpen ? 'active' : ''}`}
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Menü"
                    >
                        <span className="hamburger-line" />
                        <span className="hamburger-line" />
                        <span className="hamburger-line" />
                    </button>

                    {/* Left: Logo + Nav Links */}
                    <div className="navbar-left">
                        <Link to="/" className="navbar-brand">
                            <img src="/logo_noora.png" alt="Noora Privé" className="brand-icon-svg" />
                        </Link>

                        <div className="navbar-links">
                            {parentCats.map(cat => {
                                const subs = (cat.subcategories || []).filter(s => s.isActive);
                                const isOpen = megaOpen === cat.id;
                                if (subs.length === 0) {
                                    return (
                                        <Link
                                            key={cat.id}
                                            to={`/urunler?category=${cat.id}`}
                                            className="nav-link"
                                        >
                                            {cat.name}
                                        </Link>
                                    );
                                }
                                return (
                                    <div
                                        key={cat.id}
                                        className="nav-cat-wrap"
                                        onMouseEnter={() => openMega(cat.id)}
                                        onMouseLeave={closeMega}
                                    >
                                        <Link
                                            to={`/urunler?category=${cat.id}`}
                                            className={`nav-link nav-link-has-sub ${isOpen ? 'active' : ''}`}
                                        >
                                            {cat.name}
                                            <ChevronDown size={12} className={`nav-chevron ${isOpen ? 'open' : ''}`} />
                                        </Link>
                                        {isOpen && (
                                            <div
                                                className="nav-mega-menu"
                                                onMouseEnter={() => openMega(cat.id)}
                                                onMouseLeave={closeMega}
                                            >
                                                <div className="nav-mega-col">
                                                    <Link
                                                        to={`/urunler?category=${cat.id}`}
                                                        className="nav-mega-all"
                                                    >
                                                        Tüm {cat.name}
                                                    </Link>
                                                    {subs.map(sub => (
                                                        <Link
                                                            key={sub.id}
                                                            to={`/urunler?category=${sub.id}`}
                                                            className="nav-mega-item"
                                                        >
                                                            {sub.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {staticLinks.map(item => (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className={`nav-link ${location.pathname === item.to ? 'active' : ''}`}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Right: Search input, Theme, Favorites, Cart, Profile */}
                    <div className="navbar-actions">
                        <form className="navbar-search-form" onSubmit={handleSearch}>
                            <Search size={15} className="navbar-search-icon" />
                            <input
                                type="text"
                                className="navbar-search-input"
                                placeholder="Ürün ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </form>

                        <button className="theme-toggle-btn" onClick={toggleTheme} title={theme === 'light' ? 'Karanlık Mod' : 'Aydınlık Mod'}>
                            {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
                        </button>

                        <Link to="/favoriler" className="navbar-icon-btn" title="Favoriler">
                            <Heart size={17} />
                        </Link>

                        <button className="navbar-icon-btn cart-nav-btn" title="Sepet" onClick={() => setSidebarOpen(true)}>
                            <ShoppingBag size={17} />
                            {cart.itemCount > 0 && <span className="cart-badge">{cart.itemCount}</span>}
                        </button>

                        {/* Profile - always icon, dropdown changes based on auth */}
                        <div className="profile-dropdown-wrap" ref={profileRef}>
                            <button className="navbar-icon-btn user-nav-btn" onClick={() => setProfileOpen(!profileOpen)}>
                                <User size={17} />
                            </button>
                            {profileOpen && (
                                <div className="profile-dropdown">
                                    {customer ? (
                                        <>
                                            <div className="profile-dropdown-header">
                                                <div className="profile-dropdown-avatar">{customer.firstName[0]}{customer.lastName[0]}</div>
                                                <div>
                                                    <strong>{customer.firstName} {customer.lastName}</strong>
                                                    <span>{customer.email}</span>
                                                </div>
                                            </div>
                                            <div className="profile-dropdown-divider" />
                                            {!customer.emailVerified ? (
                                                <Link to="/eposta-dogrula" className="profile-dropdown-item profile-dropdown-verify" onClick={() => setProfileOpen(false)}>
                                                    <Mail size={15} /> E-postanızı doğrulayın
                                                </Link>
                                            ) : profileIncomplete ? (
                                                <Link to="/profil-tamamla" className="profile-dropdown-item profile-dropdown-verify" onClick={() => setProfileOpen(false)}>
                                                    <User size={15} /> Profilinizi tamamlayın
                                                </Link>
                                            ) : (
                                                <>
                                                    <Link to="/hesabim" className="profile-dropdown-item" onClick={() => setProfileOpen(false)}>
                                                        <User size={15} /> Profilim
                                                    </Link>
                                                    <Link to="/hesabim" className="profile-dropdown-item" onClick={() => setProfileOpen(false)} state={{ tab: 'orders' }}>
                                                        <Package size={15} /> Siparişlerim
                                                    </Link>
                                                    <Link to="/hesabim" className="profile-dropdown-item" onClick={() => setProfileOpen(false)} state={{ tab: 'addresses' }}>
                                                        <MapPin size={15} /> Adreslerim
                                                    </Link>
                                                </>
                                            )}
                                            <div className="profile-dropdown-divider" />
                                            <button className="profile-dropdown-item profile-dropdown-logout" onClick={handleLogout}>
                                                <LogOut size={15} /> Çıkış Yap
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <Link to="/giris" className="profile-dropdown-item" onClick={() => setProfileOpen(false)}>
                                                <User size={15} /> Giriş Yap
                                            </Link>
                                            <Link to="/kayit" className="profile-dropdown-item" onClick={() => setProfileOpen(false)}>
                                                <UserPlus size={15} /> Kayıt Ol
                                            </Link>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            <div className={`mobile-menu-backdrop ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)} />
            <div className={`mobile-menu-panel ${menuOpen ? 'open' : ''}`}>
                <div className="mobile-menu-header">
                    <img src="/logo_noora.png" alt="Noora Privé" className="mobile-menu-logo" />
                    <button className="mobile-menu-close" onClick={() => setMenuOpen(false)}>
                        <span className="close-icon">&times;</span>
                    </button>
                </div>

                <nav className="mobile-menu-nav">
                    <div className="mobile-menu-section">
                        <span className="mobile-menu-label">Koleksiyonlar</span>
                        {parentCats.map((cat) => {
                            const subs = (cat.subcategories || []).filter(s => s.isActive);
                            const isExpanded = mobileExpanded === cat.id;
                            return (
                                <div key={cat.id} className="mobile-cat-group">
                                    <div className="mobile-cat-header">
                                        <Link
                                            to={`/urunler?category=${cat.id}`}
                                            className="mobile-menu-link"
                                            onClick={() => setMenuOpen(false)}
                                        >
                                            {cat.name}
                                        </Link>
                                        {subs.length > 0 && (
                                            <button
                                                className={`mobile-cat-toggle ${isExpanded ? 'open' : ''}`}
                                                onClick={() => setMobileExpanded(isExpanded ? null : cat.id)}
                                            >
                                                <ChevronDown size={14} />
                                            </button>
                                        )}
                                    </div>
                                    {isExpanded && subs.length > 0 && (
                                        <div className="mobile-sub-links">
                                            {subs.map(sub => (
                                                <Link
                                                    key={sub.id}
                                                    to={`/urunler?category=${sub.id}`}
                                                    className="mobile-sub-link"
                                                    onClick={() => setMenuOpen(false)}
                                                >
                                                    {sub.name}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="mobile-menu-divider" />

                    <div className="mobile-menu-section">
                        {staticLinks.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`mobile-menu-link ${location.pathname === item.to ? 'active' : ''}`}
                                onClick={() => setMenuOpen(false)}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>

                    {customer && (
                        <>
                            <div className="mobile-menu-divider" />
                            <div className="mobile-menu-section">
                                <Link
                                    to={!customer.emailVerified ? '/eposta-dogrula' : profileIncomplete ? '/profil-tamamla' : '/hesabim'}
                                    className="mobile-menu-link"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    <User size={16} />
                                    <span>{!customer.emailVerified ? 'E-posta Doğrula' : profileIncomplete ? 'Profili Tamamla' : 'Hesabım'}</span>
                                </Link>
                            </div>
                        </>
                    )}
                </nav>

                <div className="mobile-menu-footer">
                    {customer ? (
                        <button className="mobile-logout-btn" onClick={handleLogout}>
                            <LogOut size={15} />
                            <span>Çıkış Yap</span>
                        </button>
                    ) : (
                        <Link to="/giris" className="mobile-auth-btn" onClick={() => setMenuOpen(false)}>
                            Giriş Yap
                        </Link>
                    )}
                    <button className="mobile-theme-toggle" onClick={toggleTheme} aria-label="Tema">
                        {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
                    </button>
                </div>
            </div>
        </>
    );
}

export function WhatsAppFloatingButton() {
    const { settings } = useSiteSettings();
    const location = useLocation();
    const isProductPage = location.pathname.startsWith('/urun/');
    if (isProductPage) return null;
    const number = settings?.whatsapp || '905521593255';
    const url = `https://wa.me/${number}`;
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                position: 'fixed', bottom: '24px', right: '24px', zIndex: 999,
                width: '54px', height: '54px', borderRadius: '50%',
                background: '#25D366', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(37,211,102,0.45)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                textDecoration: 'none'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,211,102,0.55)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,211,102,0.45)'; }}
            aria-label="WhatsApp"
        >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </a>
    );
}
